import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { adminService } from '../../services/api';

const Fields = styled.div`
  display: grid; gap: 0.8rem; margin: 1rem 0;
  label { display: grid; gap: 0.3rem; }
  select, textarea { padding: 0.55rem; width: 100%; box-sizing: border-box; }
  fieldset { border: 1px solid var(--border-subtle); border-radius: 4px; }
`;
const Actions = styled.div`display: flex; flex-wrap: wrap; gap: 8px; justify-content: flex-end; margin-top: 1rem;`;
const Table = styled.table`width: 100%; border-collapse: collapse; th, td { text-align: left; padding: 0.45rem; border-bottom: 1px solid var(--border-subtle); }`;
const statusLabel = (status) => ({ pending: 'Pending synchronization', applied: 'Applied', conflict: 'Conflict — review required', failed: 'Failed — retry available' }[status] || status || 'Pending synchronization');
const operationKey = (userId) => `tempus.lifecycle.operation.${userId}`;
const operationIdOf = (operation) => operation?.operationId || operation?.id;
const rememberOperation = (userId, operationId) => {
  // Retain only command identity in the browser, never personal decisions or
  // reasons. Server operations remain the durable source after logout/reload.
  try {
    if (operationId) sessionStorage.setItem(operationKey(userId), operationId);
    else sessionStorage.removeItem(operationKey(userId));
  } catch (_) { /* Private browsing may deny storage; the mounted dialog retains its identity. */ }
};
const savedOperation = (userId) => {
  try { return sessionStorage.getItem(operationKey(userId)); } catch (_) { return null; }
};
const newOperationId = () => {
  if (!window.crypto?.getRandomValues) throw new Error('This browser cannot create a secure request identity. Use a supported browser.');
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  // Older browsers still have Web Crypto; avoid timestamps/random counters,
  // whose collisions could bind a decision to another operator's request.
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * One review surface for employee/customer rows and the generic Users screen.
 * Preview revisions are opaque server tokens: the client cannot decide that an
 * added placement is harmless, or silently move an inclusive date boundary.
 * A timeout freezes this dialog on the original command until it is reconciled.
 */
export default function LifecycleModal({ user, action, onClose, onSaved }) {
  const [preview, setPreview] = useState(null);
  const [operation, setOperation] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [employeeOutcome, setEmployeeOutcome] = useState('');
  const [decisions, setDecisions] = useState({});
  const [reviewed, setReviewed] = useState(false);
  const commandId = useRef(savedOperation(user.id));
  const changed = useRef(onSaved);
  changed.current = onSaved;

  const acceptOperation = (result) => {
    setOperation(result);
    setError('');
    if (result.status === 'applied') rememberOperation(user.id, null);
    changed.current?.(result);
  };

  useEffect(() => {
    let live = true;
    adminService.getLifecyclePreview(user.id).then(async (data) => {
      if (!live) return;
      setPreview(data);
      if (data.coordinated) return;
      // A second tab or another staff member may have an outstanding operation.
      // Recover it before allowing a new decision, even without browser storage.
      const outstanding = data.operations?.find((item) => ['pending', 'failed', 'conflict'].includes(item.status));
      // An employee can belong to a customer batch whose subjectUserId is the
      // customer, so that parent need not appear in this subject's history.
      const id = commandId.current || operationIdOf(outstanding) || data.subject?.lifecycleOperationId;
      if (id) {
        commandId.current = id;
        setOperation(outstanding || { operationId: id, status: 'pending' });
        try {
          const result = await adminService.getLifecycleOperation(id);
          if (!live) return;
          setOperation(result);
          if (result.status === 'applied') rememberOperation(user.id, null);
        } catch (err) {
          if (live) setError(`The original request has not been confirmed. Check its status before making another decision. ${err.message}`);
        }
      }
    }).catch((err) => { if (live) setError(err.message); });
    return () => { live = false; };
  }, [user.id]);

  const subjectType = preview?.subject?.type || user.role;
  const employeeDeactivation = action === 'deactivate' && subjectType === 'employee';
  const customerDeactivation = action === 'deactivate' && subjectType === 'customer';
  const banned = String(preview?.subject?.lifecycleStatus || '').toLowerCase() === 'banned';
  // A retained operation may be reopened on another day or after a new CRM
  // snapshot. Show its original reviewed set/date beside its delivery result.
  const placements = operation?.placements || operation?.command?.reviewedPlacements || preview?.placements || [];
  const effectiveLocalDate = operation?.effectiveLocalDate || operation?.command?.effectiveLocalDate || preview?.effectiveLocalDate;
  const timeZone = operation?.timeZone || operation?.command?.timeZone || preview?.timeZone;
  const affectedEmployees = Array.from(new Map(placements.map((placement) => [String(placement.employeeId), placement])).values());
  const missingDecisions = customerDeactivation && affectedEmployees.some((placement) => {
    const choice = decisions[placement.employeeId];
    return !choice?.outcome || (choice.outcome === 'transfer' && !choice.targetCustomerId);
  });
  const blockers = (preview?.blockers || []).filter((blocker) => !blocker.action || blocker.action === action);
  if (action === 'activate' && preview?.activationEligible === false) {
    blockers.push({ code: 'ELIGIBLE_ASSIGNMENT_REQUIRED', message: 'Activation requires a reviewed assignment to an eligible CRM Customer. Review employment and customer qualification first.' });
  }
  const blocked = Boolean(operation || blockers.length || !reviewed || !reason.trim()
    || (employeeDeactivation && !employeeOutcome) || missingDecisions);
  const name = user.name || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

  const reconcile = async (retry = false) => {
    setBusy(true); setError('');
    try {
      const id = commandId.current || operationIdOf(operation);
      const result = retry ? await adminService.retryLifecycleOperation(id) : await adminService.getLifecycleOperation(id);
      acceptOperation(result);
    } catch (err) { setError(`The original request is retained. ${err.message}`); }
    finally { setBusy(false); }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (blocked || busy) return;
    setBusy(true); setError('');
    // Persist identity BEFORE sending: a response timeout must never turn the
    // next click (or reopening the dialog) into an independent command.
    let id;
    try { id = commandId.current || newOperationId(); }
    catch (err) { setError(err.message); setBusy(false); return; }
    commandId.current = id;
    rememberOperation(user.id, id);
    setOperation({ operationId: id, status: 'pending' });
    try {
      const result = await adminService.submitLifecycle(user.id, {
        contractVersion: preview.contractVersion,
        operationId: id, expectedRevision: preview.expectedRevision,
        action, reason: reason.trim(), manualConfirmation: true,
        ...(banned && action === 'activate' ? { bannedWarningAcknowledged: true } : {}),
        ...(employeeDeactivation ? { employeeOutcome } : {}),
        ...(customerDeactivation ? { decisions: affectedEmployees.map(({ employeeId }) => ({
          employeeId,
          outcome: decisions[employeeId].outcome,
          ...(decisions[employeeId].outcome === 'transfer' ? { targetCustomerId: Number(decisions[employeeId].targetCustomerId) } : {}),
        })) } : {}),
      });
      acceptOperation(result);
    } catch (err) {
      // A definitive validation rejection cannot have applied this command.
      // Keep stale reviews blocked; the operator must reopen and review anew.
      if (err.status >= 400 && err.status < 500 && err.status !== 408) {
        rememberOperation(user.id, null);
        setOperation({ operationId: id, status: 'conflict', message: err.message, canRetry: false });
        setError(err.message);
      } else {
        setError('The response was interrupted. Checking the original request; do not submit another decision.');
        try { acceptOperation(await adminService.getLifecycleOperation(id)); }
        catch (_) { /* Keep the original ID and visible Pending state for later reconciliation. */ }
      }
    } finally { setBusy(false); }
  };

  const legacySubmit = async () => {
    setBusy(true); setError('');
    try {
      await adminService[action === 'activate' ? 'activateUser' : 'deactivateUser'](user.id);
      changed.current?.({ status: 'applied', legacy: true });
      onClose();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  if (preview?.coordinated) return <Modal title="Manage status in CRM" onClose={onClose}><p>Manage employment and customer status in CRM.</p><Button onClick={onClose}>Close</Button></Modal>;
  return <Modal size="lg" title={`${operation ? 'Lifecycle decision for' : action === 'activate' ? 'Activate' : 'Deactivate'} ${name}`} onClose={busy ? () => {} : onClose}>
    {error && <p role="alert">{error}</p>}
    {!preview ? <p>{error ? 'Account details could not be loaded. Close and try again.' : 'Loading lifecycle and assignments…'}</p> : !preview.enabled && !operation ? <>
      <p>Coordinated lifecycle decisions are not enabled. This action changes Tempus account access only.</p>
      <Actions><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button onClick={legacySubmit} disabled={busy}>{action === 'activate' ? 'Activate account' : 'Deactivate account'}</Button></Actions>
    </> : <>
      <p>Effective employment date: <strong>{effectiveLocalDate}</strong> ({timeZone}). Current employment includes this day; planned assignments are reviewed separately. Account access changes when the decision is applied.</p>
      <p>Business status: <strong>{preview.subject?.lifecycleStatus || 'Unresolved — CRM review required'}</strong>. Account access: {preview.subject?.isActive ? 'Active' : 'Inactive'}.</p>
      {action === 'deactivate' && <p>Unpaid work remains payable. Use Admin correction for outstanding historical hours.</p>}
      {customerDeactivation && <p>Resolve every current and planned employee assignment. Once synchronized, the customer becomes Former in CRM and this Tempus account is deactivated.</p>}
      {action === 'activate' && <p>Activation must pass the normal identity and eligible customer assignment checks. It does not create a placement or bypass first-login setup.</p>}
      {banned && action === 'activate' && <p role="alert"><strong>Banned account:</strong> this deliberate manual activation requests removal of the access restriction. Review the eligible assignments below before confirming.</p>}
      <h3>Current and planned assignments</h3>
      {placements.length ? <div style={{ overflowX: 'auto' }}><Table aria-label="Affected assignments"><thead><tr><th>Employee</th><th>Customer</th><th>Start</th><th>End</th><th>Status</th></tr></thead><tbody>
        {placements.map((placement) => <tr key={placement.id}><td>{placement.employeeName || `Employee ${placement.employeeId}`}</td><td>{placement.customerName || `Customer ${placement.customerId}`}</td><td>{placement.startDate}</td><td>{placement.endDate || 'Open ended'}</td><td>{placement.planned ? 'Planned' : placement.status}</td></tr>)}
      </tbody></Table></div> : <p>No current or planned assignments.</p>}
      {blockers.length > 0 && <div role="alert"><p>This decision needs resolution before submission:</p><ul>{blockers.map((blocker, index) => <li key={index}>{typeof blocker === 'string' ? blocker : blocker.message || blocker.code}</li>)}</ul></div>}
      {operation ? <section aria-label="Lifecycle operation result">
        <p role="status"><strong>{statusLabel(operation.status)}</strong></p>
        {operation.message && <p>{operation.message}</p>}
        {operation.status === 'pending' && <p>The decision is retained until CRM and Tempus are reconciled. Completion has not been confirmed.</p>}
        {operation.lastError === 'CRM_LIFECYCLE_CONTRACT_REQUIRED' && <p>Synchronization is awaiting coordinated CRM setup. Checking this request keeps its original decision and dates.</p>}
        {operation.status === 'conflict' && <p>Review the affected assignments and current CRM facts before making a superseding decision.</p>}
        {operation.items?.length > 0 && <ul>{operation.items.map((item, index) => <li key={item.itemId || index}>{item.employeeName || `Employee ${item.employeeId}`}: {item.outcome || item.desiredStatus} — {statusLabel(item.status)}{item.message ? `. ${item.message}` : ''}</li>)}</ul>}
        <p>Operation: <code>{operationIdOf(operation)}</code></p>
        <Actions>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Close</Button>
          {operation.status !== 'applied' && <Button variant="secondary" onClick={() => reconcile()} disabled={busy}>Check original request</Button>}
          {operation.canRetry && <Button onClick={() => reconcile(true)} disabled={busy}>Retry original decision</Button>}
        </Actions>
      </section> : <form onSubmit={submit}>
        <Fields>
          {employeeDeactivation && <label>Employee outcome<select required value={employeeOutcome} onChange={(event) => setEmployeeOutcome(event.target.value)} disabled={busy}>
            <option value="">Choose an outcome</option><option value="former">Former</option><option value="available">Available</option><option value="banned">Banned</option>
          </select></label>}
          {customerDeactivation && affectedEmployees.map((placement) => <fieldset key={placement.employeeId}><legend>{placement.employeeName || `Employee ${placement.employeeId}`}</legend>
            <label>Outcome for {placement.employeeName || `Employee ${placement.employeeId}`}<select required value={decisions[placement.employeeId]?.outcome || ''} disabled={busy} onChange={(event) => setDecisions({ ...decisions, [placement.employeeId]: { outcome: event.target.value } })}>
              <option value="">Choose an outcome</option><option value="transfer">Transfer to another Customer</option><option value="former">Former</option><option value="available">Available</option>
            </select></label>
            {decisions[placement.employeeId]?.outcome === 'transfer' && <label>Transfer customer for {placement.employeeName || `Employee ${placement.employeeId}`}<select required value={decisions[placement.employeeId]?.targetCustomerId || ''} disabled={busy} onChange={(event) => setDecisions({ ...decisions, [placement.employeeId]: { ...decisions[placement.employeeId], targetCustomerId: event.target.value } })}>
              <option value="">Choose an eligible Customer</option>{preview.eligibleCustomers?.map((customer) => <option key={customer.id} value={customer.id}>{customer.companyName || customer.name}</option>)}
            </select></label>}
          </fieldset>)}
          <label>Reason<textarea required maxLength={1000} value={reason} disabled={busy} onChange={(event) => setReason(event.target.value)} /></label>
        </Fields>
        <label style={{ display: 'flex', gap: 8 }}><input type="checkbox" checked={reviewed} disabled={busy} onChange={(event) => setReviewed(event.target.checked)} />I have reviewed all affected assignments and confirm this manual decision{banned && action === 'activate' ? ', including the Banned warning' : ''}.</label>
        <Actions><Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button type="submit" disabled={busy || blocked}>Confirm {action === 'activate' ? 'activation' : 'deactivation'}</Button></Actions>
      </form>}
    </>}
  </Modal>;
}
